import { MAX_FLOW_STEPS, nonSubmittingExits } from './magnetCompletion.js';
import { STEP_TYPE_VALUES } from '../metadata/stepTypes.js';
import { validateStepAnswer } from '../metadata/magnetAnswers.js';

// Array order is the entry/fall-through path; only single-select choices branch.
// Return a structured first problem so the editor can open the offending card.
export function validateMagnetFlow(steps, { allowUnsubmittedContacts = false, allowIncomplete = false } = {}) {
  const issue = (index, code, message, field = 'options') => ({ index, code, message: `Step ${index + 1}: ${message}`, field });
  if (!Array.isArray(steps) || !steps.length) return issue(0, 'empty_flow', 'add at least one step.');
  if (steps.length > MAX_FLOW_STEPS) return issue(0, 'flow_limit', 'this flow is too large to validate. Split it into smaller routes.');
  if (steps.some(s => !s || typeof s !== 'object' || Array.isArray(s))) return issue(0, 'invalid_step', 'each step must be an object.');
  if (steps.reduce((n, s) => n + (Array.isArray(s.step_options) ? s.step_options.length : 0), 0) > 2000) return issue(0, 'flow_limit', 'this flow has too many choices to validate.');
  const ids = new Map();
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const id = String(step?.id ?? '');
    if (!id.trim()) return issue(i, 'missing_id', 'a stable step ID is required.');
    if (ids.has(id)) return issue(i, 'duplicate_id', `ID “${id}” is already used by step ${ids.get(id) + 1}.`);
    ids.set(id, i);
    if (!STEP_TYPE_VALUES.has(step?.step_type)) return issue(i, 'unsupported_type', 'choose a supported step type.');
  }
  const edges = steps.map(() => []);
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i], type = step.step_type, cfg = step.step_config || {};
    if (cfg.group_with_previous != null && typeof cfg.group_with_previous !== 'boolean') return issue(i, 'invalid_group', 'choose whether to show this question with the previous one.', 'group');
    if (cfg.group_heading != null && (typeof cfg.group_heading !== 'string' || cfg.group_heading.length > 120)) return issue(i, 'invalid_group', 'use a section heading of 120 characters or fewer.', 'group');
    if (cfg.group_with_previous) {
      const prev = steps[i - 1];
      if (!prev || !['text', 'email', 'phone'].includes(type) || !['text', 'email', 'phone'].includes(prev.step_type)) return issue(i, 'invalid_group', 'only adjacent text, email and phone questions can share a screen. Show this question separately.', 'group');
      if (cfg.skip_if_prefilled || prev.step_config?.skip_if_prefilled) return issue(i, 'invalid_group', 'turn off “Skip when this answer is already available” for questions sharing a screen.', 'group');
      if (steps.some(source => (Array.isArray(source.step_options) ? source.step_options : []).some(option => String(option?.goto || '') === String(step.id)))) return issue(i, 'group_branch', 'send choices to the first question in this group, or show this question separately.', 'group');
    }
    if (step.step_columns != null && !Array.isArray(step.step_columns)) return issue(i, 'invalid_field', 'answer fields must be a list.', 'field');
    const cols = Array.isArray(step.step_columns) ? step.step_columns : [];
    const options = Array.isArray(step.step_options) ? step.step_options : [];
    const choice = type === 'single_select' || type === 'multi_select';
    if (!allowIncomplete && type !== 'message' && !choice && !cols.length) return issue(i, 'missing_field', 'choose which field this answer saves to.', 'field');
    if (cols.some(k => typeof k !== 'string' || !k.trim() || ['__proto__', 'constructor', 'prototype'].includes(k))) return issue(i, 'invalid_field', 'choose a valid answer field.', 'field');
    if (new Set(cols).size !== cols.length) return issue(i, 'duplicate_mapping', 'the same answer field is mapped twice.', 'field');
    const answerProblem = validateStepAnswer(step);
    if (answerProblem) return issue(i, answerProblem.code, answerProblem.message, answerProblem.field);
    if (options.some(o => !o || typeof o !== 'object' || Array.isArray(o))) return issue(i, 'invalid_choice', 'each choice must be an object.');
    if (!allowIncomplete && choice && (!options.length || options.some(o => !String(o.label ?? '').trim()))) return issue(i, 'empty_choices', 'add a label for every choice.');
    if (!allowIncomplete && choice && new Set(options.map(o => String(o.label))).size !== options.length) return issue(i, 'duplicate_choices', 'give each choice a different label.');
    if (type === 'multi_select' && options.some(o => o.goto && String(o.goto) !== String(steps[i + 1]?.id))) return issue(i, 'multi_branch', 'use Multiple choice for branching; Select all continues to the next step.');
    if (type === 'message' && cfg.terminal) continue;
    if (type === 'single_select') {
      for (const option of options) {
        if (option.goto != null && String(option.goto) !== '') {
          const target = ids.get(String(option.goto));
          if (target === undefined) return issue(i, 'missing_target', `choice “${option.label}” points to missing step “${option.goto}”. Choose a replacement destination.`);
          edges[i].push(target);
        } else if (i + 1 < steps.length) edges[i].push(i + 1);
      }
      if (cfg.skip_if_prefilled && i + 1 < steps.length) edges[i].push(i + 1);
    } else if (i + 1 < steps.length) edges[i].push(i + 1);
  }
  const visiting = new Set(), visited = new Set();
  function cycle(i) {
    if (visiting.has(i)) return i;
    if (visited.has(i)) return -1;
    visiting.add(i);
    for (const next of edges[i]) { const hit = cycle(next); if (hit >= 0) return hit; }
    visiting.delete(i); visited.add(i); return -1;
  }
  for (let i = 0; i < steps.length; i++) {
    const hit = cycle(i);
    if (hit >= 0) return issue(hit, 'cycle', 'this branch returns to an earlier question. Choose a destination that ends the flow without looping.');
  }
  // Two mutually exclusive paths may reuse a field. Only a reachable later write
  // is a conflict; the later step must explicitly opt in to replacing the answer.
  const fields = steps.map(s => s.step_type === 'message' ? [] : (s.step_columns || []).flatMap(k => k === 'name' ? ['first_name', 'last_name'] : [k]));
  const reachable = new Set();
  const walk = i => { if (reachable.has(i)) return; reachable.add(i); edges[i].forEach(walk); };
  walk(0);
  for (const i of reachable) {
    const seen = new Set();
    function overlap(j) {
      if (seen.has(j)) return null;
      seen.add(j);
      const key = fields[i].find(k => fields[j].includes(k));
      const skipsExisting = steps[j].step_config?.skip_if_prefilled && fields[j].every(k => fields[i].includes(k));
      if (key && !skipsExisting && steps[j].step_config?.allow_overwrite !== true) return issue(j, 'answer_overwrite', `“${key}” already receives an answer at step ${i + 1}. Choose another field or explicitly allow replacement.`, 'field');
      for (const next of edges[j]) { const found = overlap(next); if (found) return found; }
      return null;
    }
    for (const next of edges[i]) { const found = overlap(next); if (found) return found; }
  }
  if (!allowUnsubmittedContacts) {
    const unsent = nonSubmittingExits(steps).find(exit => exit.code === 'unsent_contact');
    if (unsent) return unsent;
  }
  return null;
}

// Legacy unchanged routes remain readable/editable for appearance. A changed route
// or activation must be valid, including a separately authored chat entry/variant.
export function validateMagnetPackageFlows(pkg, previous, { activation = false, allowUnsubmittedContacts = false, allowIncomplete = false } = {}) {
  if (pkg?.mag_routes != null && (typeof pkg.mag_routes !== 'object' || Array.isArray(pkg.mag_routes) || Object.keys(pkg.mag_routes).length > 20)) return { code: 'invalid_routes', message: 'Named routes must be an object with at most 20 routes.' };
  if (pkg?.mag_ab?.enabled && (!pkg.mag_ab.b || typeof pkg.mag_ab.b !== 'object' || Array.isArray(pkg.mag_ab.b))) return { code: 'invalid_variant', message: 'The enabled B variant needs a valid definition.' };
  const flows = p => ({ chat: p?.mag_macro_steps, ...Object.fromEntries(Object.entries(p?.mag_routes || {}).map(([k, v]) => [`route:${k}`, v])) });
  const before = flows(previous);
  for (const [route, steps] of Object.entries(flows(pkg))) {
    if (steps === undefined) continue;
    if (!activation && JSON.stringify(steps) === JSON.stringify(before[route])) continue;
    const problem = validateMagnetFlow(steps, { allowUnsubmittedContacts, allowIncomplete });
    if (problem) return { ...problem, route, message: `${route}: ${problem.message}` };
    if (activation && !allowIncomplete) {
      for (const [index, step] of steps.entries()) {
        if (!Array.isArray(step.step_prompts) || !step.step_prompts.some(prompt => String(prompt).trim())) return { code: 'missing_prompt', route, index, field: 'prompts', message: `${route}: Step ${index + 1}: add what the magnet should say.` };
        if (step.step_type === 'message' && step.step_config?.terminal && Boolean(String(step.step_config.cta?.label || '').trim()) !== Boolean(String(step.step_config.cta?.url || '').trim())) {
          return { code: 'incomplete_link', route, index, field: 'cta', message: `${route}: Step ${index + 1}: complete the link label and URL, or leave both blank.` };
        }
      }
    }
  }
  if (pkg?.mag_ab?.enabled && pkg.mag_ab.b) {
    return validateMagnetPackageFlows({ ...pkg, ...pkg.mag_ab.b, mag_ab: null }, previous?.mag_ab?.enabled
      ? { ...previous, ...previous.mag_ab.b, mag_ab: null } : undefined, { activation, allowUnsubmittedContacts, allowIncomplete });
  }
  return null;
}
